import React from 'react'
import PropTypes from 'prop-types'
import { RadioGroup, Switch } from '@headlessui/react'
import { ExclamationCircleIcon, CheckCircleIcon } from '@heroicons/react/solid'

import classNames from '@/utils/classNamesLocal'
import TipTapTextarea from '@/shared/TipTapTextarea'

// Pay section shared by the project form and the role form. Both register the
// same field names on a react-hook-form instance, so the component takes the
// form object and reads/writes through it. `payment` is controlled by the
// caller because the radio value lives outside the form's DOM inputs.

const CURRENCY_SYMBOL = { GBP: '£', EUR: '€' }
const HOURS_PER = { Hourly: 1, Daily: 8, Weekly: 40, Monthly: 160 }
// Must match the numericality bound on Role and Project.
const MAX_AMOUNT = 2147483647

export const payEstimate = ({ paymentInterval, paymentAmountMax, expectedCommitmentAmount, expectedCommitmentInterval }) => {
  const amount = parseFloat(paymentAmountMax)
  const commitment = parseFloat(expectedCommitmentAmount)
  if (!(amount >= 0) || !(commitment > 0)) { return null }
  const hours = commitment * (expectedCommitmentInterval === 'hours' ? 1 : 8)
  const hourly = paymentInterval === 'Flat Rate' ? amount / hours : amount / (HOURS_PER[paymentInterval] || 1)
  const total = paymentInterval === 'Flat Rate' ? amount : hourly * hours
  const round = (n) => Math.round(n * 100) / 100
  return { hourly: round(hourly), total: round(total), low: hourly < 20 }
}

// Amount inputs unmount rather than unregister, so react-hook-form keeps
// whatever was last typed into a field that's no longer on screen -- switching
// off Paid, or off "It's a range", both leave a value the user can't see or
// correct. Drop what the current toggles don't render before posting.
export const withoutHiddenPay = (data) => ({
  ...data,
  payment_amount_min: data.payment === 'paid' && data.payment_range ? data.payment_amount_min : undefined,
  payment_amount_max: data.payment === 'paid' ? data.payment_amount_max : undefined,
  expected_commitment_amount: data.payment === 'paid' ? data.expected_commitment_amount : undefined
})

const PAYMENT_OPTIONS = [
  ['paid', 'Paid', 'I\'m paying people for their work.'],
  ['deferred', 'Deferred', 'Depends how the project does. Profit share or a promised amount.'],
  ['unpaid', 'Unpaid', 'Passion project. No money changes hands.']
]

const selectClass = 'block w-full rounded-md border-gray-300 bg-white py-2 px-3 shadow-sm focus:border-cccpurple focus:outline-none focus:ring-cccpurple dark:bg-gray-900 dark:text-gray-100 sm:text-sm'

const Toggle = ({ checked, onChange, label }) => (
  <Switch.Group as='div' className='flex items-center gap-x-3'>
    <Switch checked={!!checked} onChange={onChange}
      className={classNames(checked ? 'bg-cccpurple' : 'bg-gray-300 dark:bg-gray-500', 'relative inline-flex h-6 w-11 flex-shrink-0 cursor-pointer rounded-full border-2 border-transparent transition-colors focus:outline-none focus:ring-2 focus:ring-cccpurple focus:ring-offset-2')}>
      <span aria-hidden='true' className={classNames(checked ? 'translate-x-5' : 'translate-x-0', 'pointer-events-none inline-block h-5 w-5 transform rounded-full bg-white shadow transition')} />
    </Switch>
    <Switch.Label as='span' className='text-sm text-gray-700 dark:text-gray-300'>{label}</Switch.Label>
  </Switch.Group>
)

const PaymentFields = ({ form, payment, onPaymentChange, tipTapIdx, subject }) => {
  const { register, watch, getValues, setValue, formState: { errors } } = form
  const paymentDetails = watch('payment_details')
  const paymentCurrency = watch('payment_currency') || 'USD'
  const paymentRange = watch('payment_range')
  const estimate = payEstimate({
    paymentInterval: watch('payment_interval'),
    paymentAmountMax: watch('payment_amount_max'),
    expectedCommitmentAmount: watch('expected_commitment_amount'),
    expectedCommitmentInterval: watch('expected_commitment_interval')
  })
  const symbol = CURRENCY_SYMBOL[paymentCurrency] || '$'
  const toggle = (key) => setValue(key, !getValues()[key], { shouldDirty: true, shouldValidate: true })

  // A plain function, not a component: an inline component would remount on
  // every keystroke and drop focus.
  const moneyInput = (name, hint) => <div key={name}>
    <div className='relative'>
      <span className='pointer-events-none absolute inset-y-0 left-0 flex items-center pl-3 text-sm text-gray-500'>{symbol}</span>
      <input type='number' min='0' max={MAX_AMOUNT} step='any' {...register(name, { required: true, max: MAX_AMOUNT })} className='block w-full rounded-md border-gray-300 pl-7 pr-12 focus:border-cccpurple focus:ring-cccpurple sm:text-sm' placeholder='20' />
      <span className='pointer-events-none absolute inset-y-0 right-0 flex items-center pr-3 text-sm text-gray-500'>{paymentCurrency}</span>
    </div>
    { errors[name] && <div className='mt-1 text-xs text-red-600'>{errors[name][0] || "That's more money than exists. Try a smaller number."}</div> }
    { hint && <span className='mt-1 text-xs text-gray-500 dark:text-gray-400'>{hint}</span> }
  </div>

  return <div className='space-y-4'>
    <RadioGroup value={payment} onChange={onPaymentChange}>
      <RadioGroup.Label className='block text-sm font-medium text-gray-700 dark:text-gray-200'>Is this {subject} paid?</RadioGroup.Label>
      <div className='mt-2 grid gap-2 sm:grid-cols-3'>
        { PAYMENT_OPTIONS.map(([value, label, blurb]) => (
          <RadioGroup.Option key={value} value={value} className={({ checked }) => classNames('cursor-pointer rounded-lg border-2 p-3 focus:outline-none focus:ring-2 focus:ring-cccblue',
            checked ? 'border-cccblue bg-cccblue/10' : 'border-gray-200 hover:border-gray-300 dark:border-gray-600 dark:hover:border-gray-500')}>
            {({ checked }) => <>
              <div className='flex items-center gap-x-2'>
                <span className={classNames('flex h-4 w-4 items-center justify-center rounded-full border', checked ? 'border-cccblue bg-cccblue' : 'border-gray-300 bg-white')} aria-hidden='true'>
                  { checked && <span className='h-1.5 w-1.5 rounded-full bg-white' /> }
                </span>
                <RadioGroup.Label as='span' className='font-semibold text-gray-900 dark:text-white'>{label}</RadioGroup.Label>
              </div>
              <RadioGroup.Description as='p' className='mt-1 text-xs text-gray-600 dark:text-gray-300'>{blurb}</RadioGroup.Description>
            </>}
          </RadioGroup.Option>
        ))}
      </div>
    </RadioGroup>

    { payment === 'deferred' && <div>
      <label htmlFor='payment_deferred_type' className='block text-sm font-medium text-gray-700 dark:text-gray-200'>Profit share or an agreed amount?</label>
      <select id='payment_deferred_type' {...register('payment_deferred_type')} className={classNames(selectClass, 'mt-1 max-w-sm')}>
        <option value='profit-share'>Profit share</option>
        <option value='agreed-amount'>Agreed amount, paid if the project succeeds</option>
      </select>
    </div> }

    { payment === 'paid' && <div className='space-y-4 rounded-lg border border-gray-200 p-3 dark:border-gray-600'>
      <div>
        <label className='block text-sm font-medium text-gray-700 dark:text-gray-200'>How much?</label>
        <div className={classNames('mt-1 grid gap-3', paymentRange ? 'grid-cols-2 sm:grid-cols-4' : 'grid-cols-3')}>
          <select {...register('payment_interval', { required: true })} className={selectClass}>
            <option>Flat Rate</option><option>Hourly</option><option>Daily</option><option>Weekly</option><option>Monthly</option>
          </select>
          <select {...register('payment_currency', { required: true })} className={selectClass}>
            <option value='USD'>US Dollar</option><option value='GBP'>Pound Sterling</option><option value='CAD'>Canadian Dollar</option><option value='EUR'>Euro</option><option value='AUD'>Australian Dollar</option>
          </select>
          { paymentRange && moneyInput('payment_amount_min', 'Min') }
          { moneyInput('payment_amount_max', paymentRange ? 'Max' : null) }
        </div>
        <div className='mt-2'><Toggle checked={paymentRange} onChange={() => toggle('payment_range')} label="It's a range" /></div>
      </div>

      <div>
        <label className='block text-sm font-medium text-gray-700 dark:text-gray-200'>How long will the work take?</label>
        <div className='mt-1 grid grid-cols-2 gap-3 sm:max-w-sm'>
          <div>
            <input type='number' min='1' step='any' {...register('expected_commitment_amount', { validate: { positive: (v) => parseFloat(v) > 0 } })} className='block w-full rounded-md border-gray-300 focus:border-cccpurple focus:ring-cccpurple sm:text-sm' placeholder='2' />
            { errors.expected_commitment_amount && <div className='mt-1 text-xs text-red-600'>Needs to be more than zero. Even a tiny bit counts.</div> }
          </div>
          <select {...register('expected_commitment_interval')} className={selectClass}><option>hours</option><option>days</option></select>
        </div>
        <span className='text-xs text-gray-500 dark:text-gray-400'>Under an hour? Call it 1 hour.</span>
      </div>

      { estimate && <div className={classNames('flex items-start gap-x-3 rounded-md p-3 text-sm', estimate.low ? 'bg-yellow-50 text-yellow-800 dark:bg-yellow-900/30 dark:text-yellow-100' : 'bg-green-50 text-green-800 dark:bg-green-900/30 dark:text-green-100')}>
        { estimate.low ? <ExclamationCircleIcon className='h-5 w-5 flex-shrink-0' /> : <CheckCircleIcon className='h-5 w-5 flex-shrink-0' /> }
        <div className='space-y-1'>
          <div>That works out to <b>{symbol}{estimate.hourly} {paymentCurrency} an hour</b>, about <b>{symbol}{estimate.total} {paymentCurrency}</b> total.</div>
          { estimate.low && <div>That's on the low side. Expect fewer, and less experienced, submissions.</div> }
        </div>
      </div> }
    </div> }

    <div>
      <label className='block text-sm font-medium text-gray-700 dark:text-gray-200'>Anything else about pay or the contract?</label>
      <div className='mt-1'>
        <TipTapTextarea html={paymentDetails} idx={tipTapIdx} updateHtml={(val) => setValue('payment_details', val, { shouldDirty: true, shouldValidate: true })}
          placeholder='Meals and travel included. Requires an NDA. Paid on delivery. That sort of thing.' />
      </div>
    </div>
  </div>
}

export default PaymentFields

PaymentFields.propTypes = {
  form: PropTypes.object.isRequired,
  payment: PropTypes.string,
  onPaymentChange: PropTypes.func.isRequired,
  tipTapIdx: PropTypes.number,
  subject: PropTypes.string
}

PaymentFields.defaultProps = { subject: 'project' }
